import { uniqBy } from 'lodash-es';
import type { CreativeEntry } from 'types/shared';
import type { CreateScalingEgAdsParams } from '../api';
import type { CreativePayloadEntry } from '../api/types';
import type { AdSetEntry } from '../api/getAdSets';
import type { FacebookFormData } from '../validation/facebookValidator';
import type { FacebookAdSubmitContext } from './facebookAdSubmitContext';

const buildCreativesPayload = (
  data: FacebookFormData,
  creativesById: Map<string, CreativeEntry>,
): {
  creatives: CreativePayloadEntry[];
  missingCreativeIds: string[];
} => {
  const missingCreativeIds = new Set<string>();

  const creatives = data.selectedCreatives
    .map((creativeId) => {
      const creative = creativesById.get(String(creativeId)) as
        | CreativePayloadEntry
        | undefined;

      if (!creative) {
        missingCreativeIds.add(String(creativeId));
        return null;
      }

      return {
        id: creative.id,
        creative_id: creative.creative_id,
        name: creative.name,
        creative_type: creative.creative_type,
        thumbnail_url: creative.thumbnail_url ?? null,
      };
    })
    .filter((creative): creative is CreativePayloadEntry => !!creative);

  return {
    creatives,
    missingCreativeIds: Array.from(missingCreativeIds),
  };
};

const buildScalingEgAdSetsPayload = (
  data: FacebookFormData,
  cachedAdSets: AdSetEntry[],
): AdSetEntry[] | null => {
  const selectedAdSets = data.selectedAdSets
    ? uniqBy(cachedAdSets, 'id').filter((adSet) =>
      data.selectedAdSets?.find(({ id }) => id === adSet.id),
    )
    : null;

  if (!selectedAdSets) {
    return null;
  }

  return selectedAdSets.map((adSet) => {
    const campaignStatus = data.selectedCampaigns?.find(
      ({ id }) => id === adSet.campaign_id,
    )?.status;

    const status = data.selectedAdSets?.find(({ id }) => id === adSet.id)?.status;

    const budget = adSet?.is_cbo
      ? Number(data.selectedCampaignsBudgets?.[adSet.campaign_id] ?? '')
      : Number(data.selectedAdSetsBudgets?.[adSet.id] ?? '');

    return {
      ...adSet,
      budget,
      status,
      campaign_status: campaignStatus,
    };
  }) as AdSetEntry[];
};

export const getScalingEgMissingCreativeIds = (
  data: FacebookFormData,
  context: FacebookAdSubmitContext,
): string[] => buildCreativesPayload(data, context.creativesById).missingCreativeIds;

export const buildCreateScalingEgAdsPayload = (
  data: FacebookFormData,
  context: FacebookAdSubmitContext,
): CreateScalingEgAdsParams => {
  const { creatives } = buildCreativesPayload(data, context.creativesById);

  return {
    app: data.app,
    platform: data.platform,
    ad_type: data.adType ?? '',
    call_to_action: data.callToActions,
    creatives,
    ad_sets: buildScalingEgAdSetsPayload(data, context.cachedAdSets),
    hotjar: data.hotjar,
    conversion_domain: data.selectedConversionDomain,
    optional_ad_label: data.adLabelSuffix,
    ad_settings: data.selectedAdSettings[0] ?? {},
  };
};
